// WebGL2 renderer of the "Làm đẹp" retouch (parameters: booth/beauty.ts).
//
// One full-screen pass. Two output modes:
//   packed  2w × h : left = retouched colour, right = the person mask warped by
//                    the same geometry (so "mặt thon" never drags background in
//                    through the mask edge) — drawn straight into the packed frame
//   still   w × h  : retouched colour only (full-resolution cut-out)
// Falls back to "no retouch" (returns null) when WebGL2 is unavailable.

import type { BeautyParams } from '../booth/beauty';

const VS = `#version 300 es
in vec2 a;
out vec2 vUv;
void main() { vUv = vec2(a.x * 0.5 + 0.5, 0.5 - a.y * 0.5); gl_Position = vec4(a, 0.0, 1.0); }`;

const FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uCam;
uniform sampler2D uMask;
uniform int uPacked;
uniform vec2 uSize;
uniform vec4 uFace;
uniform vec3 uEye[2];
uniform int uEyeN;
uniform float uEyeK;
uniform vec3 uSlimC[4];
uniform vec2 uSlimD[4];
uniform int uSlimN;
uniform vec4 uProt[3];
uniform float uSmooth, uRad, uBright, uRosy, uMilk;
uniform int uTaps;
out vec4 o;

vec2 warp(vec2 p) {
  for (int i = 0; i < 4; i++) {
    if (i >= uSlimN) break;
    vec2 d = p - uSlimC[i].xy;
    float q = dot(d, d) / (uSlimC[i].z * uSlimC[i].z);
    if (q < 1.0) { float f = (1.0 - q) * (1.0 - q); p -= uSlimD[i] * f; }
  }
  for (int i = 0; i < 2; i++) {
    if (i >= uEyeN) break;
    vec2 d = p - uEye[i].xy;
    float q = dot(d, d) / (uEye[i].z * uEye[i].z);
    if (q < 1.0) p = uEye[i].xy + d * (1.0 - uEyeK * (1.0 - q));
  }
  return p;
}

float ell(vec2 p, vec4 e) { return length((p - e.xy) / e.zw); }

float skinProb(vec3 c) {
  float y = dot(c, vec3(0.299, 0.587, 0.114));
  float cb = (c.b - y) * 0.564 + 0.5;
  float cr = (c.r - y) * 0.713 + 0.5;
  return smoothstep(0.26, 0.31, cb) * (1.0 - smoothstep(0.50, 0.54, cb))
       * smoothstep(0.50, 0.535, cr) * (1.0 - smoothstep(0.68, 0.72, cr))
       * smoothstep(0.06, 0.18, y);
}

void main() {
  vec2 uv = vUv;
  bool maskHalf = false;
  if (uPacked == 1) { uv.x *= 2.0; if (uv.x >= 1.0) { uv.x -= 1.0; maskHalf = true; } }
  vec2 p = warp(uv * uSize);
  vec2 st = p / uSize;
  if (maskHalf) { o = vec4(vec3(texture(uMask, st).r), 1.0); return; }

  vec3 c = texture(uCam, st).rgb;
  float region = 1.0 - smoothstep(0.85, 1.12, ell(p, uFace));
  if (region <= 0.0) { o = vec4(c, 1.0); return; }
  float prot = 1.0;
  for (int i = 0; i < 3; i++) prot *= smoothstep(0.75, 1.05, ell(p, uProt[i]));
  float skin = skinProb(c) * region;

  if (uSmooth > 0.0 && skin * prot > 0.01) {
    vec3 acc = c; float ws = 1.0;
    float n = float(uTaps);
    for (int i = 0; i < 24; i++) {
      if (i >= uTaps) break;
      float fi = float(i);
      float a = fi * 2.39996323;
      float r = uRad * sqrt((fi + 0.5) / n);
      vec3 s = texture(uCam, (p + vec2(cos(a), sin(a)) * r) / uSize).rgb;
      vec3 dd = s - c;
      float w = exp(-dot(dd, dd) * 90.0);
      acc += s * w; ws += w;
    }
    vec3 sm = acc / ws;
    // keep a little texture so skin does not look like plastic
    c = mix(c, sm, clamp(uSmooth * 0.9, 0.0, 0.9) * skin * prot);
  }
  if (uBright > 0.0) {
    float beta = 1.0 + 5.0 * uBright;
    vec3 b = log(c * (beta - 1.0) + 1.0) / log(beta);
    c = mix(c, b, skin * 0.75);
  }
  if (uRosy > 0.0) c = mix(c, c * vec3(1.045, 0.965, 0.985) + vec3(0.015, 0.0, 0.008), uRosy * skin);
  if (uMilk > 0.0) {
    float l = dot(c, vec3(0.299, 0.587, 0.114));
    c = mix(c, mix(c, vec3(l), 0.35) + 0.035, uMilk * skin);
  }
  o = vec4(clamp(c, 0.0, 1.0), 1.0);
}`;

type Source = TexImageSource;

export class BeautyGL {
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private cam: WebGLTexture;
  private mask: WebGLTexture;
  private u = new Map<string, WebGLUniformLocation | null>();
  /** bilateral taps (the quality governor lowers this on slow phones) */
  taps = 16;

  private constructor(readonly canvas: HTMLCanvasElement, gl: WebGL2RenderingContext) {
    this.gl = gl;
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error('beauty shader: ' + gl.getShaderInfoLog(s));
      return s;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS));
    gl.bindAttribLocation(p, 0, 'a');
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error('beauty link: ' + gl.getProgramInfoLog(p));
    this.prog = p;
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    const tex = () => {
      const t = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return t;
    };
    this.cam = tex();
    this.mask = tex();
    gl.useProgram(p);
    gl.uniform1i(this.loc('uCam'), 0);
    gl.uniform1i(this.loc('uMask'), 1);
  }

  static create(): BeautyGL | null {
    try {
      const c = document.createElement('canvas');
      const gl = c.getContext('webgl2', { alpha: false, antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: true });
      if (!gl) return null;
      return new BeautyGL(c, gl);
    } catch (e) {
      console.warn('[beauty] unavailable', e);
      return null;
    }
  }

  private loc(n: string) {
    if (!this.u.has(n)) this.u.set(n, this.gl.getUniformLocation(this.prog, n));
    return this.u.get(n)!;
  }

  private upload(t: WebGLTexture, unit: number, src: Source) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
  }

  /**
   * Live frame: `cam` and `mask` are w × h; returns the 2w × h canvas
   * (colour | warped mask) ready to be drawn at (0, 0) of the packed frame.
   */
  renderPacked(cam: Source, mask: Source, w: number, h: number, b: BeautyParams): HTMLCanvasElement {
    this.upload(this.mask, 1, mask);
    return this.draw(cam, w, h, b, true);
  }

  /** Still: retouched w × h colour image. */
  renderStill(cam: Source, w: number, h: number, b: BeautyParams): HTMLCanvasElement {
    return this.draw(cam, w, h, b, false);
  }

  private draw(cam: Source, w: number, h: number, b: BeautyParams, packed: boolean) {
    const gl = this.gl, c = this.canvas;
    const cw = packed ? w * 2 : w;
    if (c.width !== cw || c.height !== h) { c.width = cw; c.height = h; }
    gl.viewport(0, 0, cw, h);
    gl.useProgram(this.prog);
    this.upload(this.cam, 0, cam);
    gl.uniform1i(this.loc('uPacked'), packed ? 1 : 0);
    gl.uniform2f(this.loc('uSize'), w, h);
    gl.uniform4f(this.loc('uFace'), ...b.face);
    const eyes = new Float32Array(6);
    b.eyes.forEach((e, i) => eyes.set(e, i * 3));
    gl.uniform3fv(this.loc('uEye'), eyes);
    gl.uniform1i(this.loc('uEyeN'), b.eyes.length);
    gl.uniform1f(this.loc('uEyeK'), b.eyeK);
    const sc = new Float32Array(12), sd = new Float32Array(8);
    b.slim.forEach(([x, y, r, dx, dy], i) => { sc.set([x, y, r], i * 3); sd.set([dx, dy], i * 2); });
    gl.uniform3fv(this.loc('uSlimC'), sc);
    gl.uniform2fv(this.loc('uSlimD'), sd);
    gl.uniform1i(this.loc('uSlimN'), b.slim.length);
    const pr = new Float32Array(12);
    b.protect.slice(0, 3).forEach((e, i) => pr.set(e, i * 4));
    gl.uniform4fv(this.loc('uProt'), pr);
    gl.uniform1f(this.loc('uSmooth'), b.smooth);
    gl.uniform1f(this.loc('uRad'), b.radius);
    gl.uniform1f(this.loc('uBright'), b.bright);
    gl.uniform1f(this.loc('uRosy'), b.rosy);
    gl.uniform1f(this.loc('uMilk'), b.milk);
    gl.uniform1i(this.loc('uTaps'), Math.max(4, Math.min(24, this.taps)));
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    return c;
  }
}
